import type { ComputedRef } from 'vue';

export enum RadioGroupVariant {
  Radio = 'radio',
  Button = 'button',
  Chip = 'chip',
  Tile = 'tile',
}

export type RadioGroupVariants = `${RadioGroupVariant}`;

/**
 * An individual radio `<input>` within a RadioGroup instance
 */
export interface RadioGroupOption {
  /**
   * Disables the RadioGroupOption if truthy
   */
  disabled?: boolean;

  /**
   * The unique identifier for the option
   */
  id: number | string;

  /**
   * The text to be displayed for the option
   */
  text: string;

  /**
   * The value of the option. Used for the modelValue of the RadioGroup.
   */
  value: string;

  /**
   * The subtitle for tile variant
   */
  subTitle?: string;

  /**
   * The subtext for tile variant
   */
  subText?: string;
}

/**
 * Properties and utilities provided to children of a RadioGroup instance
 */
export interface RadioGroupInjection {
  /**
   * This type should match RadioGroupProps['disabled']
   */
  disabled: ComputedRef<boolean | undefined>;

  /**
   * This type should match RadioGroupProps['fullWidth']
   */
  fullWidth: ComputedRef<boolean | undefined>;

  /**
   * This type should match RadioGroupProps['modelValue']
   */
  modelValue: ComputedRef<string | undefined>;

  /**
   * This type should match RadioGroupProps['name']
   */
  name: ComputedRef<string | undefined>;

  /**
   * This type should match RadioGroupProps['options']
   */
  options: ComputedRef<RadioGroupOption[] | undefined>;

  /**
   * Triggered when the user changes their selection; updates the v-model.
   */
  update: (e: Event) => void;

  /**
   * This type should match RadioGroupProps['variant']
   * @default 'radio'
   */
  variant: ComputedRef<RadioGroupVariants | undefined>;

  /**
   * Default orientation. If the variant is `button` and the group is wider than its parent it will be displayed in a column, and this prop will be ignored.
   *
   * Note: This does not work with the deprecated `options` prop. Update those instances to use the `RadioNew` component and default slot.
   */
  orientation?: ComputedRef<'horizontal' | 'vertical'>;
}
